import { http } from '@/utils'
import type { ReqDeleteMusicTask } from '@/api/type/params'
export namespace CreaticonMusicApi {
  export interface Result {
    taskId: string
    taskStatus: number
  }

  export interface Params {
    prompt: string
    customMode: boolean
    instrumental: boolean
    tags: string[]
    title?: string
  }
}

export namespace GetMusicTasksPageApi {
  export interface Params {
    page: number
    limit: number
  }

  export interface Result {
    data: any[]
    total: number
    page: number
    limit: number
  }
}

export namespace GetMusicTasksApi {
  export interface Params {
    id: string
  }
  export interface Result {
    taskId: string
    taskStatus: number
    prompt: string
    title: string
    createdAt: string
    musics: any[]
  }
}

export namespace MusicToogleFavoriteApi {
  export interface Params {
    id: string
  }
}

export namespace GetMusicWorksPageApi {
  export interface Params {
    page: number
    limit: number
    musicType?: string
    orderBy?: string
    order?: string
    favorite?: boolean
  }
  export interface Result {
    data: MusicItemType[]
    total: number
    page: number
    limit: number
  }
}

export namespace DeleteMusicWorksBatchApi {
  export interface Params {
    ids: string[]
  }
}

export namespace GetMusicCreationPromptsApi {
  export interface Params {
    lang: string
    count: number
  }
  export interface Result {
    statusCode: number
    message: string
    data: {
      title: string
      prompt: string
    }[]
  }
}

export namespace GetMusicCreationTagsApi {
  export interface Params {
    lang: string
  }
  export interface Result {
    statusCode: number
    message: string
    data: {
      category: string
      tags: {
        value: string
        name: string
      }[]
    }[]
  }
}

export namespace DeleteMusicTaskApi {
  export interface Params {
    ids: string[]
    recursive: boolean
  }
}
export type MusicItemType = {
  createdAt: string
  duration: number
  favorite: boolean
  fileUrl: string
  instrumental: boolean
  lycris: string
  musicId: string
  musicType: string
  public: boolean
  tags: string[]
  thumbnailUrl: string
  title: string
  voiceClone?: {
    createdAt: string
    modelType: number
    name: string
    thumbnailUrl: string
    trainStatus: number
    voiceId: string
  }
}
export namespace RenameMusicApi {
  export interface Params {
    id: string
    name: string
  }
  export interface Result {
    statusCode: number
    message: string
    data: MusicItemType[]
  }
}
export namespace RenameMusicMaterialApi {
  export interface Params {
    id: string
    name: string
  }
  export interface Result {
    createdAt: string
    modelType: number
    name: string
    thumbnailUrl: string
    trainStatus: number
    voiceId: string
  }
}

export namespace CreateReverbMusicApi {
  export interface Params {
    id: string
    url: string
  }
}

export namespace UpdateMusicLyricsApi {
  export interface Params {
    lycris: string
  }
}

/**
 * 批量删除音乐创作
 * @param params 音乐任务ID
 * @returns
 */
export async function reqDeleteMusicWorksBatch(
  data: DeleteMusicWorksBatchApi.Params,
): Promise<any> {
  return http.delete<DeleteMusicWorksBatchApi.Params, any>(`music_works/batch`, { data })
}

/**
 * 获取用户音乐创作
 * @param params 音乐任务ID
 * @returns
 */
export async function reqGetMusicWorksPage(
  params: GetMusicWorksPageApi.Params,
): Promise<GetMusicWorksPageApi.Result> {
  return http.get<GetMusicWorksPageApi.Params, GetMusicWorksPageApi.Result>(`music_works`, {
    params,
  })
}

/**
 * 收藏音乐或取消
 * @param params 音乐任务ID
 * @returns
 */
export async function reqMusicToogleFavorite(params: MusicToogleFavoriteApi.Params): Promise<any> {
  return http.post<MusicToogleFavoriteApi.Params, any>(`music_works/${params.id}/toggle_favorite`)
}

/**
 *  创建音乐
 */
export async function reqCreaticonMusic(
  data: CreaticonMusicApi.Params,
): Promise<CreaticonMusicApi.Result> {
  return http.post<object, CreaticonMusicApi.Result>(`music_creation/create`, { data })
}

/**
 * 获取用户音乐任务列表
 * @param params 分页参数
 */
export async function reqGetMusicTasksPage(
  params: GetMusicTasksPageApi.Params,
): Promise<GetMusicTasksPageApi.Result> {
  return http.get<GetMusicTasksPageApi.Params, GetMusicTasksPageApi.Result>(
    `music_creation/tasks`,
    { params },
  )
}

/**
 * 获取音乐任务详情
 * @param params 音乐任务ID
 * @returns
 */
export async function reqGetMusicTasks(
  params: GetMusicTasksApi.Params,
): Promise<GetMusicTasksApi.Result> {
  return http.get<GetMusicTasksApi.Params, GetMusicTasksApi.Result>(
    `music_creation/tasks/${params.id}`,
  )
}

/**
 * 获取音乐创作提示词
 * @returns
 */
export async function reqGetMusicCreationPrompts(
  params: GetMusicCreationPromptsApi.Params,
): Promise<GetMusicCreationPromptsApi.Result> {
  return http.get<GetMusicCreationPromptsApi.Params, GetMusicCreationPromptsApi.Result>(
    `music_creation/prompts`,
    { params },
  )
}

/**
 * 音乐创作标签
 * @returns
 */
export async function reqGetMusicCreationTags(
  params: GetMusicCreationTagsApi.Params,
): Promise<GetMusicCreationTagsApi.Result> {
  return http.get<GetMusicCreationTagsApi.Params, GetMusicCreationTagsApi.Result>(
    `music_creation/tags`,
    { params },
  )
}

/**
 * 删除音乐创作任务
 * @param data 音乐创作任务ID
 * @returns
 */
export async function reqDeleteMusicTask(data: ReqDeleteMusicTask): Promise<any> {
  return http.delete<ReqDeleteMusicTask, any>(`music_creation`, { data })
}

/**
 * 重命名音乐
 * @param id 音乐创作ID
 * @param data 重命名参数
 * @returns
 */
export async function reqRenameMusic(id: string, data: RenameMusicApi.Params): Promise<any> {
  return http.patch<RenameMusicApi.Params, RenameMusicApi.Result>(`music_works/${id}`, {
    data,
  })
}

/**
 * 重命名音乐素材
 * @param id 音乐素材ID
 * @param data 重命名参数
 * @returns
 */
export async function reqRenameMusicMaterial(
  id: string,
  data: RenameMusicMaterialApi.Params,
): Promise<any> {
  return http.patch<RenameMusicMaterialApi.Params, RenameMusicMaterialApi.Result>(
    `user_asset/${id}`,
    {
      data,
    },
  )
}

/**
 * 重命名声线
 * @param id 声线ID
 * @param data 重命名参数
 * @returns
 */
export async function reqRenameVoiceClone(
  id: string,
  data: RenameMusicMaterialApi.Params,
): Promise<any> {
  return http.patch<RenameMusicMaterialApi.Params, RenameMusicMaterialApi.Result>(
    `voice_clone/${id}`,
    {
      data,
    },
  )
}

/**
 * 创建混响音乐
 * @param id 音乐创作ID
 * @param data 混响音乐参数
 * @returns
 */
export async function reqCreateReverbMusic(
  id: string,
  data: CreateReverbMusicApi.Params,
): Promise<any> {
  return http.post<CreateReverbMusicApi.Params, any>(`music_works/${id}/remix_music`, { data })
}

/**
 * 更新音乐歌词
 * @param id 音乐创作ID
 * @param data 歌词参数
 * @returns
 */
export async function reqUpdateMusicLyrics(
  id: string,
  data: UpdateMusicLyricsApi.Params,
): Promise<any> {
  return http.patch<UpdateMusicLyricsApi.Params, any>(`music_works/${id}`, { data })
}

/**
 * 获取歌曲详情
 * @param id 歌曲ID
 * @returns
 */
export async function reqGetMusicDetail(id: string): Promise<any> {
  return http.get<any, any>(`music_works/${id}`)
}

/**
 * 获取首页推荐
 * @returns
 */
export async function reqGetHomeMusicRecommend(): Promise<any> {
  return http.get<any, any>(`music_works/show_case`)
}

/**
 * 发布音乐
 * @param id 音乐ID
 * @returns
 */
export async function reqPublishMusic(id: string): Promise<any> {
  return http.post<any, any>(`music_works/${id}/publish`)
}

/**
 * 获取分享音乐详情
 * @param id 音乐ID
 * @returns
 */
export async function reqGetShareMusicDetail(id: string): Promise<any> {
  return http.get<any, any>(`music_works/${id}`)
}
